/**
 * Utility Functions for Data Formatting
 * Helper functions for formatting dates, text, numbers, etc.
 */

/**
 * Format date to readable string
 * @param {string|Date} date - Date to format
 * @param {boolean} includeTime - Whether to include time
 * @returns {string} Formatted date string
 */
export const formatDate = (date, includeTime = false) => {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  
  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }
  
  return dateObj.toLocaleDateString('en-US', options);
};

/**
 * Format time ago (e.g., "2 hours ago")
 * @param {string|Date} date - Date to format
 * @returns {string} Time ago string
 */
export const formatTimeAgo = (date) => {
  if (!date) return 'N/A';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const seconds = Math.floor((now - dateObj) / 1000);
  
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
  };
  
  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval >= 1) {
      return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
    }
  }
  
  return 'Just now';
};

/**
 * Capitalize first letter of string
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Format user role for display
 * @param {string} role - User role
 * @returns {string} Formatted role
 */
export const formatRole = (role) => {
  if (!role) return 'N/A';
  
  const roleMap = {
    parent: 'Parent',
    coaching_teacher: 'Coaching Teacher',
    school_admin: 'School Admin',
    school_teacher: 'School Teacher',
    super_admin: 'Super Admin',
  };
  
  return roleMap[role] || capitalize(role);
};

/**
 * Format task state for display
 * @param {string} state - Task state
 * @returns {string} Formatted state
 */
export const formatTaskState = (state) => {
  if (!state) return 'N/A';
  
  const stateMap = {
    assigned: 'Assigned',
    submitted: 'Submitted',
    evaluated: 'Evaluated',
    published: 'Published',
  };
  
  return stateMap[state] || capitalize(state);
};

/**
 * Format score/percentage
 * @param {number} score - Score value
 * @param {number} total - Total possible score
 * @returns {string} Formatted score
 */
export const formatScore = (score, total = 100) => {
  if (score === null || score === undefined) return 'N/A';
  
  const percentage = total ? Math.round((score / total) * 100) : score;
  return `${percentage}%`;
};

/**
 * Resolve a backend-relative API URL (e.g. "/api/teaching/shared-notes/files/xyz")
 * into an absolute URL using REACT_APP_BACKEND_URL, so <img>/<a> tags load
 * correctly regardless of whether the frontend and backend share an origin.
 * @param {string} url - Backend-relative or absolute URL
 * @returns {string} Absolute URL
 */
export const resolveApiFileUrl = (url) => {
  if (!url) return url;
  if (!url.startsWith('/api/')) return url;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
};

/**
 * Format file size
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};


/**
 * Format a task due date using the same display rules as task cards.
 * Date-only deadlines (stored at midnight) are shown as dates; deadlines with
 * an explicit time include both date and time.
 * @param {string|Date|null|undefined} dateValue - Due date value to format
 * @returns {string|null} Formatted due date label, or null when invalid/missing
 */
export const formatTaskDueDateTime = (dateValue) => {
  if (!dateValue) return null;
  const dueDate = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(dueDate.getTime())) return null;

  const hasDueTime = dueDate.getHours() !== 0 || dueDate.getMinutes() !== 0;
  return hasDueTime
    ? dueDate.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : dueDate.toLocaleDateString();
};

export const formatTaskDueDate = (dateValue) => {
  if (!dateValue) return null;
  const dueDate = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(dueDate.getTime())) return null;

  return dueDate.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
};

/**
 * Determine whether a pending task is overdue.
 * @param {Object} task - Task object containing due_date
 * @param {Date|number} nowValue - Optional current time override
 * @returns {boolean} True when the due date exists, is valid, and has passed
 */
export const isTaskOverdue = (task, nowValue = new Date()) => {
  if (!task?.due_date) return false;
  const dueDate = new Date(task.due_date);
  if (Number.isNaN(dueDate.getTime())) return false;
  const nowTimestamp = nowValue instanceof Date ? nowValue.getTime() : nowValue;
  return !Number.isNaN(nowTimestamp) && nowTimestamp > dueDate.getTime();
};

/**
 * Determine whether a submitted task came in after its due date.
 * @param {Object} task - Task object containing due_date and submitted_at
 * @returns {boolean} True when submitted_at is valid and later than due_date
 */
export const isTaskSubmittedLate = (task) => {
  if (!task?.due_date || !task?.submitted_at) return false;
  const dueTimestamp = new Date(task.due_date).getTime();
  const submittedTimestamp = new Date(task.submitted_at).getTime();
  return (
    !Number.isNaN(dueTimestamp) &&
    !Number.isNaN(submittedTimestamp) &&
    submittedTimestamp > dueTimestamp
  );
};

/**
 * Truncate task title with ellipsis and fallback
 * @param {string} title - Task title
 * @param {number} maxLength - Maximum characters to show
 * @returns {string} Truncated title
 */
export const truncateTaskTitle = (title, maxLength = 35, options = {}) => {
  const { fallback = 'Untitled Task', suffix = '...' } = options;

  if (!title) return fallback;

  const normalizedTitle = String(title).trim();
  if (!normalizedTitle) return fallback;
  if (normalizedTitle.length <= maxLength) return normalizedTitle;

  const safeMaxLength = Math.max(0, maxLength - String(suffix).length);
  return `${normalizedTitle.slice(0, safeMaxLength)}${suffix}`;
};

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
export const truncate = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Format topics list and truncate if too long
 * @param {string[]|string|null|undefined} topics - Topics list or string
 * @param {Object} options - Formatting options
 * @param {number} options.maxWords - Maximum number of words to display
 * @param {string} options.fallback - Fallback text when topics are missing
 * @returns {string} Formatted topics string
 */
export const formatTopics = (topics, { maxWords = 10, fallback = 'All Topics' } = {}) => {
  if (!topics) return fallback;

  const topicsText = Array.isArray(topics) ? topics.join(', ') : String(topics);
  if (!topicsText.trim()) return fallback;

  const words = topicsText.split(/\s+/);
  if (words.length > maxWords) {
    return `${words.slice(0, maxWords).join(' ')} etc.`;
  }

  return topicsText;
};

/**
 * Normalize grade values for reliable filtering/comparison.
 * @param {string|number|null|undefined} gradeValue - Grade value like "Grade 5", "5", "class-5"
 * @returns {string|null} Normalized grade token (numeric when available), or null when empty
 */
export const normalizeGrade = (gradeValue) => {
  if (gradeValue === null || gradeValue === undefined || gradeValue === '') return null;
  const raw = String(gradeValue).trim();
  if (!raw) return null;
  const match = raw.match(/(\d+)/);
  return match ? match[1] : raw.toLowerCase();
};

/**
 * Format grade label for display.
 * @param {string|number|null|undefined} gradeValue
 * @returns {string} Display value like "Grade 5"
 */
export const formatGradeLabel = (gradeValue) => {
  const normalized = normalizeGrade(gradeValue);
  if (!normalized) return '';
  return /^\d+$/.test(normalized) ? `Grade ${normalized}` : normalized;
};

/**
 * Format class/section label with grade prefix.
 * @param {string} name - Class/section name (e.g., A, B, C)
 * @param {string|number} grade - Grade number
 * @returns {string} Formatted label like "Grade 5 A"
 */
export const formatGradeSectionName = (name, grade) => {
  if (!name) return '';
  const trimmed = String(name).trim();
  if (!trimmed) return '';
  if (grade === null || grade === undefined || grade === '') {
    return trimmed;
  }
  return `Class ${grade}-${trimmed}`;
};

const compactLabelParts = (parts) => parts
  .map((part) => (part === null || part === undefined ? '' : String(part).trim()))
  .filter(Boolean);

const firstAvailable = (...values) => compactLabelParts(values)[0] || '';

/**
 * Format the current generation/share target for modal headers.
 * @param {Object|null|undefined} target - Child, student, batch, or class object.
 * @param {Object} options
 * @param {string} options.userRole
 * @param {string} options.subject
 * @param {string|number} options.grade
 * @returns {string} Header value after "For: ".
 */
export const formatTargetContextLabel = (target, { userRole = 'parent', subject = '', grade = '' } = {}) => {
  const isTeacher = ['coaching_teacher', 'school_teacher'].includes(userRole);
  const targetGrade = firstAvailable(grade, target?.grade, target?.class_grade);
  const subjectLabel = firstAvailable(subject, target?.subject);
  const classGrade = normalizeGrade(targetGrade) || targetGrade;

  const directName = firstAvailable(
    target?.name,
    target?.child_name,
    target?.student_name,
    target?.full_name,
    target?.title
  );

  const classOrBatchName = firstAvailable(
    target?.batch_name,
    target?.batchName,
    target?.class_name,
    target?.className,
    target?.section,
    target?.batch?.name,
    target?.class?.name
  );

  const primaryLabel = isTeacher && directName && !classOrBatchName
    ? (userRole === 'school_teacher' ? formatGradeSectionName(directName, classGrade) : directName)
    : directName;

  const classLabel = classOrBatchName
    ? (userRole === 'school_teacher' ? formatGradeSectionName(classOrBatchName, classGrade) : classOrBatchName)
    : '';

  const audienceLabel = compactLabelParts([primaryLabel, classLabel]).join(' / ');
  const detailParts = compactLabelParts([
    subjectLabel,
    formatGradeLabel(targetGrade)
  ]);

  return compactLabelParts([audienceLabel, ...detailParts]).join(' • ');
};

/**
 * Format name (handle both name and full_name fields)
 * @param {Object} user - User object
 * @returns {string} Formatted name
 */
export const formatUserName = (user) => {
  if (!user) return 'N/A';
  return user.name || user.full_name || user.email || 'Unknown';
};

/**
 * Get initials from name
 * @param {string} name - Full name
 * @returns {string} Initials
 */
export const getInitials = (name) => {
  if (!name) return '?';
  
  const parts = name.split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Get the first name from a full name string
 * @param {string} name - Full name
 * @returns {string} First name only
 */
export const getFirstName = (name) => {
  if (!name) return '';

  const trimmedName = name.trim();
  if (!trimmedName) return '';

  return trimmedName.split(/\s+/)[0];
};

/**
 * Generate random color for avatars
 * @param {string} seed - Seed string (e.g., user ID)
 * @returns {string} Hex color
 */
export const getColorFromString = (seed) => {
  if (!seed) return '#6B7280';
  
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const colors = [
    '#EF4444', '#F59E0B', '#10B981', '#3B82F6', 
    '#8B5CF6', '#EC4899', '#14B8A6', '#F97316',
  ];
  
  return colors[Math.abs(hash) % colors.length];
};

/**
 * Format array to comma-separated string
 * @param {Array} arr - Array to format
 * @param {string} separator - Separator character
 * @returns {string} Formatted string
 */
export const formatList = (arr, separator = ', ') => {
  if (!arr || !Array.isArray(arr)) return '';
  return arr.join(separator);
};

/**
 * Parse error message from API response
 * @param {Object} error - Error object
 * @returns {string} Error message
 */
export const getErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred';
  
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An error occurred while processing your request';
};

export default {
  formatDate,
  formatTimeAgo,
  capitalize,
  formatRole,
  formatTaskState,
  formatScore,
  formatFileSize,
  resolveApiFileUrl,
  truncate,
  truncateTaskTitle,
  formatUserName,
  getInitials,
  getFirstName,
  getColorFromString,
  formatList,
  getErrorMessage,
};
